/**
 * The docked property card — the reference-style building interface.
 *
 * Selecting anything on the map surfaces this fixed card at the bottom
 * right: building summary, every room still for sale across its floors with
 * gold prices, and the walk-inside call to action. Clicking a room's $ (or a
 * row here) opens the centred BUY ROOM dialog. The draggable inspector
 * window remains the deep-management tool, opened from the dock.
 */

import type { FloorPlanResponse, PropertyDetail } from "@parcelbound/contracts";
import { useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import { api, ApiError } from "./api";
import { fmtMoneyRound, npcLandlord } from "./format";
import { useApp } from "./store";
import type { WorldRenderer } from "./world/renderer";

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : "something went wrong";
}

interface SaleRoom {
  id: string;
  label: string;
  floor: number;
  areaM2: number;
  price: string;
}

export function PropertyPanel(): JSX.Element | null {
  const selectedId = useApp((s) => s.selectedId);
  const planFloor = useApp((s) => s.planFloor);
  const setFloorPlan = useApp((s) => s.setFloorPlan);
  const notify = useApp((s) => s.notify);

  const [detail, setDetail] = useState<PropertyDetail | null>(null);
  const [saleRooms, setSaleRooms] = useState<SaleRoom[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);

  // The card follows the selection.
  useEffect(() => {
    setDetail(null);
    setSaleRooms([]);
    if (!selectedId) return;
    let live = true;
    api.property(selectedId).then(
      (d) => {
        if (live) setDetail(d);
      },
      (err: unknown) => notify("err", describe(err)),
    );
    return () => {
      live = false;
    };
  }, [selectedId, refreshTick]);

  // The panel owns floor-plan fetching for the cutaway (the inspector's
  // section reads the same store state without fetching on its own).
  const buildingId = detail?.building?.id ?? null;
  useEffect(() => {
    if (!buildingId) return;
    let live = true;
    api.floorPlan(buildingId, planFloor).then(
      (p) => {
        if (live) setFloorPlan(p);
      },
      () => {
        if (live) setFloorPlan(null);
      },
    );
    return () => {
      live = false;
    };
  }, [buildingId, planFloor, setFloorPlan, refreshTick]);

  // Every floor's sellable rooms, for the ROOMS FOR SALE list.
  const floors = detail?.building?.floors ?? 0;
  useEffect(() => {
    if (!buildingId || floors === 0) return;
    let live = true;
    const wanted = Math.min(floors, 10);
    Promise.all(
      Array.from({ length: wanted }, (_, f) => api.floorPlan(buildingId, f).catch(() => null)),
    ).then((plans) => {
      if (!live) return;
      const rooms: SaleRoom[] = [];
      for (const plan of plans) {
        if (!plan) continue;
        for (const unit of plan.units) {
          for (const room of unit.rooms) {
            if (!room.sellable || room.owner) continue;
            rooms.push({
              id: room.id,
              label: room.label,
              floor: room.floor,
              areaM2: room.areaM2,
              price: room.appraisedValue,
            });
          }
        }
      }
      setSaleRooms(rooms);
    });
    return () => {
      live = false;
    };
  }, [buildingId, floors, refreshTick]);

  if (!selectedId || !detail) return null;
  return (
    <>
      {detail.building ? (
        <BuildingCard detail={detail} saleRooms={saleRooms} />
      ) : (
        <LandCard detail={detail} onChanged={() => setRefreshTick((n) => n + 1)} />
      )}
      {/* Outside the card on purpose: the card's backdrop-filter would
       * otherwise become the containing block and centre the modal inside it. */}
      <BuyRoomDialog detail={detail} onBought={() => setRefreshTick((n) => n + 1)} />
    </>
  );
}

function world(): WorldRenderer | null {
  return (window as { __pbWorld?: WorldRenderer }).__pbWorld ?? null;
}

function BuildingCard({
  detail,
  saleRooms,
}: {
  detail: PropertyDetail;
  saleRooms: SaleRoom[];
}): JSX.Element {
  const b = detail.building!;
  const p = detail.parcel;
  const selectRoom = useApp((s) => s.selectRoom);
  const setPlanFloor = useApp((s) => s.setPlanFloor);
  const toggleWindow = useApp((s) => s.toggleWindow);

  const cheapest = useMemo(
    () => saleRooms.reduce<number | null>((min, r) => {
      const v = Number(r.price);
      return min === null || v < min ? v : min;
    }, null),
    [saleRooms],
  );

  // Slide the building clear of the card as soon as it is selected, so its
  // room markers are actually reachable.
  useEffect(() => {
    world()?.frameBuilding(b.centroid);
  }, [b.id]);

  return (
    <aside className="prop-card" aria-label={b.label}>
      <h2>{b.label}</h2>
      <p className="sub">
        {b.label} · {b.floors} floor{b.floors === 1 ? "" : "s"} ·{" "}
        {Math.round(b.footprintM2 * b.floors).toLocaleString("en-US")} m²
      </p>

      <div className="row">
        <span>Appraised value</span>
        <span className="val">{fmtMoneyRound(b.appraisedValue)}</span>
      </div>
      <div className="row">
        <span>Foot traffic</span>
        <span className="val">{p.busyness.toFixed(1)}x</span>
      </div>
      <p className="owned">
        Owned by {b.owner ? b.owner.displayName : `${npcLandlord(b.id)} (NPC landlord)`}
      </p>

      <hr />

      <div className="rooms-head">
        <span>Rooms for sale ({saleRooms.length})</span>
        <span>{cheapest === null ? "" : `from ${fmtMoneyRound(cheapest)}`}</span>
      </div>
      <div className="rooms-list">
        {saleRooms.map((r) => (
          <button
            key={r.id}
            className="sale-row"
            onClick={() => {
              setPlanFloor(r.floor);
              selectRoom(r.id);
            }}
          >
            <span className="nm">{r.label}</span>
            <span className="meta">
              {" "}· Fl {r.floor} · {Math.round(r.areaM2)} m²
            </span>
            <span className="px">{fmtMoneyRound(r.price)}</span>
          </button>
        ))}
        {saleRooms.length === 0 && <p className="none">Every room here has an owner.</p>}
      </div>
      <p className="hint">Click a green $ on a room to buy it.</p>

      <button
        className="walk"
        onClick={() => {
          world()?.frameBuilding(b.centroid, 150);
        }}
      >
        Walk inside to buy
      </button>
      <button className="more" onClick={() => toggleWindow("inspector")}>
        Manage property…
      </button>
    </aside>
  );
}

function LandCard({
  detail,
  onChanged,
}: {
  detail: PropertyDetail;
  onChanged: () => void;
}): JSX.Element {
  const p = detail.parcel;
  const notify = useApp((s) => s.notify);
  const toggleWindow = useApp((s) => s.toggleWindow);
  const [busy, setBusy] = useState(false);
  const session = useApp((s) => s.session);
  const mine = p.owner?.id === session?.id;

  return (
    <aside className="prop-card" aria-label="Land parcel">
      <h2>Land parcel</h2>
      <p className="sub">
        {p.landUse} · {Math.round(p.areaM2).toLocaleString("en-US")} m²
      </p>
      <div className="row">
        <span>Appraised value</span>
        <span className="val">{fmtMoneyRound(p.appraisedValue)}</span>
      </div>
      <div className="row">
        <span>Foot traffic</span>
        <span className="val">{p.busyness.toFixed(1)}x</span>
      </div>
      <p className="owned">
        {p.owner ? `Owned by ${p.owner.displayName}${mine ? " (you)" : ""}` : "Unclaimed land"}
      </p>
      {!p.owner && (
        <button
          className="walk"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            api
              .purchase(p.id, "land", p.appraisedValue, detail.version)
              .then((r) => {
                notify("ok", `Bought the land for ${fmtMoneyRound(r.pricePaid)}.`);
                onChanged();
                return api.dashboard().then((d) => useApp.getState().setDashboard(d));
              })
              .catch((err: unknown) => notify("err", describe(err)))
              .finally(() => setBusy(false));
          }}
        >
          Buy land · {fmtMoneyRound(p.appraisedValue)}
        </button>
      )}
      <button className="more" onClick={() => toggleWindow("inspector")}>
        Manage property…
      </button>
    </aside>
  );
}

/**
 * The BUY ROOM dialog: opens whenever an unsold, sellable room is selected
 * (a $ on the plan or a row on the card), closes by deselecting.
 */
function BuyRoomDialog({
  detail,
  onBought,
}: {
  detail: PropertyDetail;
  onBought: () => void;
}): JSX.Element | null {
  const plan = useApp((s) => s.floorPlan);
  const selectedRoomId = useApp((s) => s.selectedRoomId);
  const selectRoom = useApp((s) => s.selectRoom);
  const notify = useApp((s) => s.notify);
  const [busy, setBusy] = useState(false);

  const b = detail.building;
  if (!b || !plan || plan.buildingId !== b.id) return null;
  const room = plan.units.flatMap((u) => u.rooms).find((r) => r.id === selectedRoomId);
  if (!room || !room.sellable || room.owner) return null;
  const seller = b.owner ? b.owner.displayName : npcLandlord(b.id);

  return (
    <div className="buy-room-backdrop" onClick={() => selectRoom(null)}>
      <div
        className="buy-room"
        role="dialog"
        aria-label={`Buy ${room.label}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="head">
          <span className="tag">
            <span className="dot" /> Buy room
          </span>
          <button className="x" aria-label="Close" onClick={() => selectRoom(null)}>
            ×
          </button>
        </div>

        <h3>{room.label}</h3>
        <p className="meta">
          Floor {room.floor + 1} · {Math.round(room.areaM2)} m² · 1 room
        </p>
        <p className="where">{b.label}</p>

        <p className="price">
          {fmtMoneyRound(room.appraisedValue)} <span>one time</span>
        </p>
        <span className="avail">
          <span className="dot" /> Available to buy
        </span>

        <div className="row">
          <span>Seller</span>
          <span className="val">{seller}</span>
        </div>
        <div className="row">
          <span>Price</span>
          <span className="val">{fmtMoneyRound(room.appraisedValue)}</span>
        </div>
        <div className="row">
          <span>Ongoing cost</span>
          <span className="val">None</span>
        </div>

        <button
          className="confirm"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            api
              .buyRoom(b.id, plan.floor, room.id, room.appraisedValue)
              .then(async (r) => {
                notify("ok", `${room.label} is yours for ${fmtMoneyRound(r.pricePaid)}.`);
                selectRoom(null);
                onBought();
                useApp.getState().setDashboard(await api.dashboard());
              })
              .catch((err: unknown) => notify("err", describe(err)))
              .finally(() => setBusy(false));
          }}
        >
          Buy · {fmtMoneyRound(room.appraisedValue)}
        </button>
        <p className="foot">
          One payment and the room is yours — no rent, no running costs, ever. Buying works from
          right here; no need to walk in.
        </p>
      </div>
    </div>
  );
}
